import React from 'react';
import { Link as AnchorLink } from 'evergreen-ui';
import styled from '@emotion/styled';
import { NavLink, Link as EventLink } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import {
  Pane,
  Text,
  ListItem,
  minorScale,
  majorScale,
  Heading,
  Paragraph,
} from 'evergreen-ui';

import Btn from '../btn/btn';
import { authUserVar } from '../../helpers/auth';
import Container from '../container/container';
import { TS_ACCEPT_COOKIES } from '../../constants';
import clientStorage from '../../libs/client-storage';
import { VIEWPORT_BREAKPOINTS } from '../../helpers/enums';
import { textToBoolean } from '../../helpers/functions';
import { ReactComponent as TwitterIcon } from '../../assets/icons/twitter.svg';
import { ReactComponent as FacebookIcon } from '../../assets/icons/facebooks.svg';
import WhytelionLogo from '../../assets/images/whytelion-logo.png';
import TicketsirLogo from '../../assets/images/ticketsir-logo-white-variant.png';

import { ReactComponent as InstagramIcon } from '../../assets/icons/instagram.svg';
import { ReactComponent as AppleStoreIcon } from '../../assets/icons/app-store.svg';
import { ReactComponent as GooglePlaystoreIcon } from '../../assets/icons/google-play.svg';
import { SuperAffiliateContext } from '../../providers/super-affiliate';
import { useAppContext } from '../../providers/cookie-policy';

const StyledList = styled(ListItem)`
  &:hover {
    color: var(--secondary-default-color);
  }

  @media screen and (min-width: ${VIEWPORT_BREAKPOINTS.md}px) {
    &:nth-of-type(2) {
      order: 6;
    }
  }
`;

const StyledExternalLink = styled(AnchorLink)`
  color: ${({ color }) => color || 'var(--primary-default-color)'};
  text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
  font-size: ${({ fontSize }) => fontSize || '0.9rem'};
  border: none;
  outline: none;
  box-shadow: none;
  transition: color 0.2s ease, opacity 0.2s ease;

  &:hover {
    color: ${({ hoverColor, color }) =>
      hoverColor || color || 'var(--primary-default-color)'};
    opacity: ${({ hoverOpacity }) => hoverOpacity || 0.5};
  }

  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
`;

export const AppLink = styled(AnchorLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  &:focus {
    box-shadow: none;
  }
`;

const Footer = ({ waitlistEnabled }) => {
  const authUser = useReactiveVar(authUserVar);

  const isPathDisabled = (paths) => paths.includes(window.location.pathname);

  const endsWithAny = (path, endings) => {
    return endings.some((ending) => path.endsWith(ending));
  };

  const urlPath = window.location.pathname;
  // Check if the path ends with "/buy" or "/register-interest" using the function
  const isLastSegmentMatch = endsWithAny(urlPath, [
    '/buy',
    '/register-interest',
    '/presale',
  ]);

  const isPaymentPage = window.location.pathname.includes('/payment/');

  const { superAffiliate } = React.useContext(SuperAffiliateContext);
  const businessName = superAffiliate?.businessName || 'Cokobar';
  const { isCookiesAccepted } = useAppContext();

  const isLaptop = useMediaQuery({
    query: `(min-width: ${VIEWPORT_BREAKPOINTS.md}px)`,
  });

  const hasNotAcceptedCookies = clientStorage.getItem(TS_ACCEPT_COOKIES)
    ? false
    : true;

  const FOOTER_NAV_SECTIONS = [
    {
      heading: businessName,
      items: [
        {
          shown: true,
          title: 'About Us',
          path: '/about-us',
        },
        {
          shown: true,
          title: 'How It Works',
          path: '/how-it-works',
        },
        {
          shown: true,
          title: 'Your Account',
          path: '/account/profile',
        },
      ],
    },
    {
      heading: `Using ${businessName}`,
      items: [
        {
          shown: true,
          title: 'Privacy Policy',
          path: '/privacy-policy',
        },
        {
          shown: true,
          title: 'FAQs',
          path: '/faqs',
        },
        {
          shown: true,
          title: 'Terms and Conditions',
          path: '/term-and-condition',
        },
      ],
    },
    {
      heading: 'Media and Updates',
      items: [
        {
          shown: true,
          title: 'Blog',
          path: 'https://whytelion.com/?blog=y',
          isExternal: true,
        },
        {
          shown: true,
          title: 'Contact Us',
          path: '/contact-us',
        },
        {
          shown: true,
          title: 'Help Center',
          path: '/help-center',
        },
      ],
      customComponent: (
        <Pane
          display="flex"
          alignItems="center"
          paddingY={minorScale(2)}
          columnGap={majorScale(2)}
          width="100%"
        >
          <StyledExternalLink
            href={process.env.REACT_APP_INSTAGRAM_LINK}
            target="_blank"
            rel="noreferrer"
            aria-label="follow us on instagram"
          >
            <InstagramIcon cursor="pointer" />
          </StyledExternalLink>
          <StyledExternalLink
            href={process.env.REACT_APP_TWITTER_LINK}
            target="_blank"
            rel="noreferrer"
            aria-label="follow us on twitter"
          >
            <TwitterIcon cursor="pointer" />
          </StyledExternalLink>
          <StyledExternalLink
            href={process.env.REACT_APP_FACEBOOK_LINK}
            target="_blank"
            rel="noreferrer"
            aria-label="follow us on Facebook"
          >
            <FacebookIcon cursor="pointer" />
          </StyledExternalLink>
        </Pane>
      ),
    },
    {
      heading: 'Download our apps',
      items: [
        {
          shown: textToBoolean(process.env.REACT_APP_SHOW_HOST_EVENT_LINK),
          title: 'Create your events',
          path: '/host/create-event',
          domainOnly: true,
          unauthenticatedRedirect: '/how-to-create-event',
        },
      ],
      customComponentPosition: 'top', // or 'bottom' (default)
      customComponent: (
        <Pane
          display="flex"
          flexWrap="wrap"
          marginY={majorScale(1)}
          paddingBottom={majorScale(2)}
          gap={'1.5rem'}
        >
          {textToBoolean(process.env.REACT_APP_SHOW_APP_DOWNLOAD_LINKS) && (
            <>
              <AppLink
                href={process.env.REACT_APP_APPLE_STORE_COKOBAR_LINK}
                target="_blank"
                rel="noreferrer"
                color="neutral"
                aria-label="download app on apple store"
              >
                <AppleStoreIcon cursor="pointer" width={120} />
              </AppLink>
              <AppLink
                href={process.env.REACT_APP_GOOGLE_PLAY_COKOBAR_LINK}
                target="_blank"
                rel="noreferrer"
                color="neutral"
                aria-label="download app on play store"
              >
                <GooglePlaystoreIcon cursor="pointer" width={120} />
              </AppLink>
            </>
          )}
        </Pane>
      ),
    },
  ];

  return (
    <Pane
      backgroundColor="var(--secondary-dark-color)"
      marginTop={majorScale(8)}
      className="footerSpacing"
    >
      <Container
        maxWidth="100%"
        paddingTop={hasNotAcceptedCookies ? majorScale(2) : majorScale(9)}
        fontSize="1rem"
        paddingBottom={
          isLaptop && !isCookiesAccepted
            ? majorScale(12)
            : isLaptop && waitlistEnabled
            ? majorScale(10)
            : isLaptop
            ? majorScale(5)
            : !isCookiesAccepted
            ? majorScale(18)
            : waitlistEnabled
            ? majorScale(14)
            : !isLaptop
            ? majorScale(11)
            : majorScale(1)
        }
      >
        <Pane className="row mb-md-5">
          {FOOTER_NAV_SECTIONS.map((section, sectionIndex) => (
            <Pane
              className="col-md-3 col-sm-12 mb-5 mb-md-0"
              key={sectionIndex}
            >
              <Heading
                color="#ffffff"
                fontWeight="700"
                fontSize="1em"
                marginBottom={majorScale(4)}
              >
                {section.heading}
              </Heading>

              {section.customComponent &&
                section.customComponentPosition === 'top' && (
                  <>{section.customComponent}</>
                )}

              {section.items
                .filter((navItem) => navItem.shown !== false) // Handles conditionals
                .map((navItem, i) => {
                  const key = `${sectionIndex}-${i}`;
                  if (navItem.isExternal) {
                    return (
                      <StyledList
                        key={key}
                        color="#ffffff"
                        cursor="pointer"
                        width="fit-content"
                      >
                        <StyledExternalLink
                          href={navItem.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="#ffffff"
                          hoverColor="#ffffff"
                          hoverOpacity={1}
                          paddingY="8px"
                          paddingX="0px"
                          display="inline-block"
                        >
                          {navItem.title}
                        </StyledExternalLink>
                      </StyledList>
                    );
                  }
                  if (navItem.domainOnly) {
                    return (
                      <StyledList
                        key={key}
                        color="var(--text-default-color)"
                        cursor="pointer"
                        width="fit-content"
                      >
                        {authUser?.id && authUser?.hostId ? (
                          <EventLink to={navItem.path}>
                            <Btn
                              fontSize="16px"
                              marginRight={40}
                              borderColor={'#C4C4C4'}
                              disabledcolor={'#C4C4C4'}
                              disabled={
                                isPathDisabled([
                                  '/host/create-event',
                                  '/how-to-create-event-sign-in',
                                  '/register-successfully',
                                ]) ||
                                isLastSegmentMatch ||
                                isPaymentPage
                              }
                            >
                              {navItem.title}
                            </Btn>
                          </EventLink>
                        ) : (
                          <EventLink to={navItem.unauthenticatedRedirect}>
                            <Btn
                              fontSize="16px"
                              marginRight={40}
                              borderColor={'#C4C4C4'}
                              disabledcolor={'#C4C4C4'}
                              disabled={
                                isPathDisabled([
                                  '/how-to-create-event',
                                  '/how-to-create-event-sign-in',
                                  '/register-successfully',
                                ]) ||
                                isLastSegmentMatch ||
                                isPaymentPage
                              }
                            >
                              {navItem.title}
                            </Btn>
                          </EventLink>
                        )}
                      </StyledList>
                    );
                  }

                  return (
                    <StyledList
                      key={key}
                      color="var(--text-default-color)"
                      cursor="pointer"
                      width="fit-content"
                    >
                      <NavLink
                        exact
                        to={navItem.path}
                        // activestyle={{ color: 'var(--secondary-default-color)' }}
                        style={{
                          display: 'inline-block',
                          padding: '8px 0px',
                          color: '#fff',
                          fontSize: '.9rem',
                        }}
                      >
                        {navItem.title}
                      </NavLink>
                    </StyledList>
                  );
                })}
              {/* ✅ Render custom component if provided */}
              {section.customComponent &&
                section.customComponentPosition !== 'top' && (
                  <>{section.customComponent}</>
                )}
            </Pane>
          ))}
        </Pane>

        <Pane
          borderTop="1px solid #ffffff"
          borderBottom="1px solid #ffffff"
          paddingY={majorScale(2)}
          marginY={majorScale(2)}
        >
          <Pane
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={majorScale(2)}
            flexWrap="wrap"
          >
            <Paragraph
              color="#ffffff"
              width={isLaptop ? 'auto' : '100%'}
              textAlign={isLaptop ? 'left' : 'center'}
              fontWeight="400"
              fontSize="1em"
            >
              Our Network:
            </Paragraph>
            <AnchorLink
              href="https://whytelion.com/"
              target="_blank"
              rel="noreferrer"
              color="neutral"
              aria-label="follow us on Whytelion"
              display="block"
            >
              <Pane
                is="img"
                src={WhytelionLogo}
                maxWidth="100%"
                maxHeight="40px"
                verticalAlign="middle"
                alt="Whytelion Logo"
                display="inline-block"
              />
            </AnchorLink>
            <AnchorLink
              href="https://www.ticketsir.com/"
              target="_blank"
              rel="noreferrer"
              color="neutral"
              aria-label="follow us on TicketSir"
              display="block"
            >
              <Pane
                is="img"
                src={TicketsirLogo}
                alt="TicketSir Logo"
                maxWidth="100%"
                maxHeight="40px"
                verticalAlign="middle"
                display="inline-block"
              />
            </AnchorLink>
          </Pane>
        </Pane>

        <Pane
          display="flex"
          justifyContent={isLaptop ? 'flex-start' : 'center'}
        >
          <Text
            is="small"
            color="var(--text-white-color)"
            textAlign="center"
            fontSize="1em"
            paddingY={minorScale(5)}
          >
            &copy; {new Date().getFullYear()} {superAffiliate.domainName} is a
            registered trademark of Whytelion Ltd.
          </Text>
        </Pane>
      </Container>
    </Pane>
  );
};

export default Footer;
